'use client';

import {
  TO_BE_DEFINED_LABEL,
  type KnockoutNode,
  type Player,
  type Team,
} from '@/types/tournament.model';
import { useState, useEffect } from 'react';
import { BracketView } from './knockout/BracketView';
import { EmptyKnockoutStage } from './knockout/EmptyKnockoutStage';
import { MatchCard } from './MatchCard';
import { useKnockoutStageData } from './knockout/useKnockoutStageData';
import { formatPlayerName } from '@/utils/team-utils';
import { useEloCategories } from '@/hooks/useEloCategories';

interface KnockoutStageProps {
  rootNode: KnockoutNode | null | undefined;
  tournamentId: string;
  teams?: Team[];
  allNodes?: Record<string, KnockoutNode>;
  onResultUpdated?: () => void;
  /** Sport UID to resolve Elo / categories in match card player popovers. */
  tournamentSportUid?: string;
  /** Fixture status from FixtureSection. When false, result load/edit is blocked. */
  isFixtureActive?: boolean;
}

export function KnockoutStage({
  rootNode,
  tournamentId,
  teams = [],
  allNodes = {},
  onResultUpdated,
  tournamentSportUid,
  isFixtureActive = true,
}: KnockoutStageProps) {
  const [expandedView, setExpandedView] = useState(false);
  const [activeRound, setActiveRound] = useState<number | null>(null);
  const eloCategories = useEloCategories(tournamentSportUid);

  // Process knockout stage data
  const { roundsByLevel, maxRound, roundNames } = useKnockoutStageData(
    allNodes,
    rootNode,
  );

  // Function to get team name from ID with comprehensive error handling
  const getTeamName = (teamId: string | Team): string => {
    if (!teamId) return TO_BE_DEFINED_LABEL;

    try {
      // If teamId is an object with an id property
      if (typeof teamId === 'object' && teamId !== null) {
        // If it has players, extract names
        if (
          teamId.players &&
          Array.isArray(teamId.players) &&
          teamId.players.length > 0
        ) {
          return teamId.players
            .map((player: Player) => {
              if (player && player.name) {
                return formatPlayerName(player.name);
              }
              return '';
            })
            .filter(Boolean)
            .join(' / ');
        }
        // If it has an id but no players
        if (teamId.id) {
          // Look for the team in the teams array
          const team = teams.find((t) => t.id === teamId.id);
          if (
            team &&
            team.players &&
            Array.isArray(team.players) &&
            team.players.length > 0
          ) {
            return team.players
              .map((player) => {
                if (player && player.name) {
                  return formatPlayerName(player.name);
                }
                return '';
              })
              .filter(Boolean)
              .join(' / ');
          }
          return TO_BE_DEFINED_LABEL;
        }
        return TO_BE_DEFINED_LABEL;
      }

      // If teamId is a string (team ID)
      if (typeof teamId === 'string') {
        // Look for the team in the teams array
        const team = teams.find((t) => t.id === teamId);
        if (
          team &&
          team.players &&
          Array.isArray(team.players) &&
          team.players.length > 0
        ) {
          return team.players
            .map((player) => {
              if (player && player.name) {
                return formatPlayerName(player.name);
              }
              return '';
            })
            .filter(Boolean)
            .join(' / ');
        }
        return TO_BE_DEFINED_LABEL;
      }

      return TO_BE_DEFINED_LABEL;
    } catch (error) {
      console.error('Error getting team name:', error);
      return TO_BE_DEFINED_LABEL;
    }
  };

  // Get available rounds for tabs
  const availableRounds = Object.keys(roundsByLevel)
    .map(Number)
    .sort((a, b) => a - b);

  // Render match cards for the active round
  const renderMatchCards = (round: number) => {
    const nodes = roundsByLevel[round] || [];

    if (nodes.length === 0) {
      return (
        <div className="rounded-md border border-gray-200 bg-gray-50 p-4 text-center">
          <p className="text-sm text-gray-500">
            No hay partidos disponibles para esta ronda.
          </p>
        </div>
      );
    }

    return (
      <div className="grid gap-4 md:grid-cols-2">
        {nodes.map((node) => {
          if (!node || !node.match) return null;

          // Extract team information
          const team1 = node.match.team1;
          const team2 = node.match.team2;

          // Check if this is a placeholder match
          const isPlaceholder = node.match.id.startsWith('placeholder_');

          // Get team names
          const team1Name = team1 ? getTeamName(team1) : TO_BE_DEFINED_LABEL;
          const team2Name = team2 ? getTeamName(team2) : TO_BE_DEFINED_LABEL;

          return (
            <div key={node.match.id}>
              <MatchCard
                match={node.match}
                team1Name={team1Name}
                team2Name={team2Name}
                isFinal={round === maxRound}
                tournamentId={tournamentId}
                onResultUpdated={onResultUpdated}
                disabled={isPlaceholder}
                canLoadResults={isFixtureActive}
                sportUid={tournamentSportUid}
                eloCategories={eloCategories}
              />
            </div>
          );
        })}
      </div>
    );
  };

  useEffect(() => {
    if (availableRounds.length > 0 && activeRound === null) {
      setActiveRound(availableRounds[0]);
    }
  }, [availableRounds, activeRound]);

  // If there's no root node, show message that there are no knockouts
  if (!rootNode) {
    console.log('KnockoutStage: No rootNode, rendering EmptyKnockoutStage');
    return <EmptyKnockoutStage />;
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-end">
        <button
          onClick={() => setExpandedView(!expandedView)}
          className="rounded-md bg-blue-50 px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-100"
        >
          {expandedView ? 'Ver tarjetas de partidos' : 'Ver bracket completo'}
        </button>
      </div>

      {expandedView ? (
        <BracketView
          roundsByLevel={roundsByLevel}
          maxRound={maxRound}
          roundNames={roundNames}
          getTeamName={getTeamName}
          tournamentId={tournamentId}
          onResultUpdated={onResultUpdated}
        />
      ) : (
        <div>
          {/* Round tabs - custom implementation without shadcn/ui */}
          <div className="mb-6">
            <div className="mb-4 flex rounded-md bg-slate-100 p-1">
              {availableRounds.map((round) => (
                <button
                  key={round}
                  onClick={() => {
                    console.log(`Setting activeRound to ${round}`);
                    setActiveRound(round);
                  }}
                  className={`flex-1 rounded-sm px-3 py-1.5 text-sm font-medium transition-all ${
                    activeRound === round
                      ? 'bg-white text-slate-950 shadow-sm'
                      : 'text-slate-500 hover:bg-slate-200 hover:text-slate-900'
                  }`}
                >
                  {roundNames[round] || `Ronda ${round}`}
                </button>
              ))}
            </div>

            {activeRound !== null && renderMatchCards(activeRound)}
          </div>
        </div>
      )}
    </div>
  );
}
